"use client";

import { Fragment } from "react";

import Link from "next/link";
import { PortableText } from "@portabletext/react";
import {
  customRichTextComponent,
  richTextComponent,
} from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { PageCompetitionAffiliateSchema } from "@/sanity/pages/PageCompetitionAffiliateTemplate";
import { motion } from "framer-motion";

import shadow from "@/public/comp_affiliate-v2/Shadow.png";
import shadowM from "@/public/comp_affiliate-v2/ShadowM.png";
import logo from "@/public/logo-black.svg";
import arrow from "@/public/global/dotted-arrow.svg";
import bannerDeco from "@/public/comp_affiliate/decoR.png";
import bannerDecoL from "@/public/comp_affiliate/decoL.png";
import Countdown from "@/components/global/countdown/Countdown";
import { cn, sanityImageUrl } from "@/lib/utils";
import ChipSerif from "@/components/global/ui/ChipSerif";
import { PageCompetitionAffiliateV2Schema } from "@/sanity/pages/PageCompetitionAffiliateV2Template";
import { Carousel, CarouselContent, CarouselItem } from "@/shadcn/ui/carousel";
import AutoScroll from "embla-carousel-auto-scroll";
import AnimatedArrows from "@/components/global/ui/AnimatedArrows";
import { getImageDimensions } from "@sanity/asset-utils";
import Image from "@/helpers/Image";

interface HeroProps {
  data: PageCompetitionAffiliateV2Schema;
  className?: string;
  showTopBanner?: boolean;
}

export default function Hero({ data, className, showTopBanner }: HeroProps) {
  return (
    <section
      id="hero-section"
      className={cn(
        "relative px-[0.85rem] lg:px-0 pt-[1rem] lg:pt-[5rem]",
        showTopBanner && "mt-[16rem] lg:mt-[7rem]",
        className,
      )}
    >
      <Image
        src={shadow}
        alt=""
        className="absolute inset-0 !w-full !h-full object-cover pointer-events-none hidden lg:block mix-blend-hard-light"
      />
      <div className="absolute bottom-0 left-0 h-[15rem] w-full bg-gradient-to-t from-white to-transparent" />
      <div className="relative overflow-clip w-full ">
        <div className="flex relative z-10 flex-col items-center px-[2rem] lg:px-0 border-[#0000000A] border lg:border-none rounded-[2rem] lg:rounded-none pt-[3.25rem] pb-[4.5rem] lg:pb-0 lg:pt-0">
          <Image
            src={shadowM}
            alt=""
            className="absolute inset-0 !w-full !h-full pointer-events-none lg:hidden rounded-[2rem]"
          />
          <div className="relative mx-auto w-full lg:px-0 z-[1] mb-[3.25rem] lg:mb-[11rem]">
            <motion.div
              className="flex items-start lg:items-center w-[min(213.25rem,100%)] mx-auto"
              initial={{ opacity: 0, filter: "blur(10px)" }}
              animate={{ opacity: 1, filter: "blur(0px)" }}
              transition={{
                duration: 0.3,
                delay: 0,
                ease: [0.25, 0.4, 0.25, 1],
              }}
            >
              <div className="w-full grid grid-cols-[15.875rem_auto] lg:grid-cols-3 items-center">
                {data?.partnerName && (
                  <div className="hidden lg:flex items-center gap-[1rem]">
                    <p className="text-[#103037]/80 text-[2rem] lg:text-[2rem] font-[500] leading-[116%] italic m-0">
                      {data?.partnerName}
                    </p>
                  </div>
                )}

                <Link href={"/"} className="w-fit justify-self-center">
                  <Image src={logo} alt="" className="!w-[15.875rem]" />
                </Link>

                {data.dates && (
                  <div className="w-fit justify-self-end bg-[#FF5F5F]/10 rounded-full py-[1.5rem] px-[1.75rem] flex items-center gap-[1rem]">
                    <div className="bg-[#ED4747] w-[0.875rem] h-[0.875rem] shrink-0 rounded-full" />
                    <p className="m-0 text-[#C51212] font-sans text-[1.75rem] font-[500] leading-[100%]">
                      {data?.dates.start} | {data?.dates.end}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
          {data?.heroChip && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="px-[2.5rem] py-[1rem] bg-[#10303708] border border-[#00000014] rounded-full mb-[2rem]
              uppercase text-[1.5rem] lg:text-[1.75rem] font-sans font-normal text-[#103037]/80"
            >
              {data.heroChip}{" "}
              <span className="text-[#1E864B]">{data.partnerName}</span>
            </motion.div>
          )}

          {data?.heroText && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 0.3,
                delay: 0.3,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
              className="richtext richtext-start lg:richtext-center w-[min(100.875rem,100%)] prose-p:w-[min(60.375rem,100%)] prose-headings:text-[5rem] lg:prose-headings:text-[8rem]
              prose-strong:text-[2rem] lg:prose-strong:text-[2.75rem] prose-strong:text-[#103037] prose-strong:font-medium
              prose-p:mb-[1.5rem] lg:prose-p:text-[2.25rem]
              [&_.inline-icon]:!w-[6.6rem] lg:[&_.inline-icon]:!w-[10.125rem] [&_.inline-icon]:!m-0 [&_.inline-icon]:rotate-[-8deg] lg:[&_.inline-icon]:rotate-[6deg]"
            >
              <PortableText
                value={data.heroText}
                components={customRichTextComponent({
                  marks: {
                    inlineImage: ({ value }) => {
                      const url = value?.image?.asset?._ref
                        ? sanityImageUrl(value?.image?.asset?._ref)
                        : "";
                      const dimensions = value?.image?.asset?._ref
                        ? getImageDimensions(value?.image?.asset?._ref)
                        : {
                            width: 0,
                            height: 0,
                            aspectRatio: 0,
                          };
                      return (
                        <div className="aspect-square inline-block mx-2 inline-icon rounded-full border border-[#DD002A] p-[0.25rem] relative translate-y-[1rem] lg:translate-y-[2rem]">
                          <Image
                            className="!m-0 flex-shrink-0 aspect-square object-cover object-top rounded-full"
                            src={{
                              src: url,
                              width: dimensions.width,
                              height: dimensions.height,
                            }}
                            alt="Icon image"
                            quality={100}
                          />

                          <div className="absolute bottom-0 translate-y-1/2 left-1/2 -translate-x-1/2 bg-[#DC002A] rounded-[0.285rem] h-[2rem] w-[4.8rem] text-center flex items-center ">
                            <span className="text-white leading-none text-[1.72075rem] w-full h-full translate-y-[0.15rem] tracking-[0.01rem]">
                              LIVE
                            </span>
                          </div>
                        </div>
                      );
                    },
                  },
                })}
              />
            </motion.div>
          )}

          {data?.heroCTA && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 0.3,
                delay: 0.5,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
              className="w-full lg:w-fit"
            >
              <CTAButton
                className="mt-[3rem] lg:min-w-[39.75rem]"
                data={data.heroCTA}
              />
            </motion.div>
          )}

          {data.toggleCountdown && (
            <div
              className="mt-[2.5rem] w-[min(70.875rem,100%)] mx-auto rounded-[1.5rem] lg:rounded-[2.25rem] backdrop-blur-[5.375rem] bg-[#0F2D33] shadow-[0_-1.072px_41.904px_0_#8DCDC0_inset]
              [&_.countdownUnitItem]:border-white/10"
            >
              <Countdown
                dateTimeString={data?.countdown ?? ""}
                lang={data.countdownTextLang ?? "de"}
                useGradientText
                useBebasFont
              />
            </div>
          )}

          <AnimatedArrows className="w-[37.125rem] mt-[4rem] mb-[14rem] hidden lg:block" />

          {/* Mobile - Static Items */}
          {data?.heroItems && (
            <div className="lg:hidden w-[min(138.375rem,100%)] mx-auto flex items-start flex-col mt-[4rem]">
              {data?.heroItems.map((item, index) => (
                <Fragment key={index}>
                  <motion.div
                    className="flex items-center gap-[1.5rem] py-[0.5rem] pl-[.5rem] pr-[2rem]"
                    initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    transition={{
                      duration: 0.3,
                      delay: 0.3 + index * 0.1,
                      ease: [0.25, 0.4, 0.25, 1],
                    }}
                  >
                    {item.icon && (
                      <div className="bg-[#6DA9911A] rounded-full px-[1.5rem] py-[1.25rem] shrink-0">
                        <Image
                          src={item.icon}
                          alt=""
                          className="!w-[2.5rem] mix-blend-multiply"
                        />
                      </div>
                    )}
                    <p className="m-0 text-[#103037]/80 font-[400] leading-[140%] text-[2rem]">
                      {item.text}
                    </p>
                  </motion.div>

                  {index < data.heroItems.length - 1 && (
                    <div className="w-full max-w-[95%] bg-black/10 mx-[1rem] h-[.125rem] my-[1rem]" />
                  )}
                </Fragment>
              ))}
            </div>
          )}

          {/* Desktop - Carousel */}
          {data?.heroItems && (
            <div className="hidden lg:block w-full mx-auto mt-[8rem]">
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                }}
                plugins={[
                  AutoScroll({
                    speed: 1,
                    stopOnInteraction: false,
                  }),
                ]}
                className="w-full"
              >
                <CarouselContent className="-ml-[2rem]">
                  {[
                    ...data?.heroItems,
                    ...data?.heroItems,
                    ...data?.heroItems,
                    ...data?.heroItems,
                  ].map((item, index) => (
                    <CarouselItem key={index} className="pl-[2rem] basis-auto">
                      <motion.div
                        className="flex items-center gap-[1.25rem] py-[0.5rem] pl-[0.5rem] pr-[2.5rem] bg-[#EDF1F1] rounded-full"
                        initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        transition={{
                          duration: 0.3,
                          delay: 0.3 + index * 0.1,
                          ease: [0.25, 0.4, 0.25, 1],
                        }}
                      >
                        {item.icon && (
                          <div className="bg-white rounded-full px-[1.5rem] py-[1.25rem]">
                            <Image
                              src={item.icon}
                              alt=""
                              className="!w-[2.5rem]"
                            />
                          </div>
                        )}
                        <p className="m-0 text-[#103037] font-[500] leading-[140%] text-[2rem] whitespace-nowrap">
                          {item.text}
                        </p>
                      </motion.div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
